/** tpl 38 "Field exists and has value" — the most structured args editor.
 *
 * Wire shape (dashboard EMPTY_FIELD_PRESENT rows):
 *   {match_all: bool, fields: [{field, value, op, negate, has_value,
 *                               field_mode, value_mode}, …]}
 */
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';

import { FIELD_EXISTS_OPS, FIELD_MODE_OPTIONS, VALUE_MODE_OPTIONS } from '../ruleCatalog';
import type { ArgEditorProps } from './common';
import { argsOf } from './common';

interface FieldRow {
  field: string;
  value: string;
  op: string;
  negate: boolean;
  has_value: boolean;
  field_mode: string;
  value_mode: string;
}

const EMPTY_ROW: FieldRow = {
  field: '',
  value: '',
  op: 'icontains',
  negate: false,
  has_value: false,
  field_mode: 'path',
  value_mode: 'constant',
};

/** Old rows may be bare strings ("field name") — the matcher accepts both. */
function normalizeRow(raw: unknown): FieldRow {
  if (typeof raw === 'string') return { ...EMPTY_ROW, field: raw };
  return { ...EMPTY_ROW, ...(raw as Partial<FieldRow>) };
}

export function FieldExistsArgs({ rule, onArgsChange, disabled }: ArgEditorProps) {
  const args = argsOf(rule);
  const rows = (Array.isArray(args.fields) ? args.fields : []).map(normalizeRow);
  const matchAll = args.match_all !== false;

  const setRows = (next: FieldRow[]) => onArgsChange({ ...args, fields: next });
  const patchRow = (idx: number, patch: Partial<FieldRow>) =>
    setRows(rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  return (
    <Stack spacing={1.5}>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={matchAll ? 'all' : 'any'}
        disabled={disabled}
        onChange={(_, v) => {
          if (v !== null) onArgsChange({ ...args, match_all: v === 'all' });
        }}
        aria-label="Field match mode"
      >
        <ToggleButton value="all">Match all fields</ToggleButton>
        <ToggleButton value="any">Match any field</ToggleButton>
      </ToggleButtonGroup>

      {rows.map((row, idx) => (
        <Box
          key={idx}
          sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5, bgcolor: 'background.default' }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={row.negate}
                  disabled={disabled}
                  onChange={(e) => patchRow(idx, { negate: e.target.checked })}
                />
              }
              label="Not"
              sx={{ mr: 0 }}
            />
            <TextField
              size="small"
              label="Field"
              sx={{ flex: 1, minWidth: 220 }}
              value={row.field}
              disabled={disabled}
              onChange={(e) => patchRow(idx, { field: e.target.value })}
            />
            <TextField
              select
              size="small"
              label="Field mode"
              sx={{ width: 190 }}
              value={row.field_mode}
              disabled={disabled}
              onChange={(e) => patchRow(idx, { field_mode: e.target.value })}
            >
              {FIELD_MODE_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>
            <IconButton
              aria-label="Remove field check"
              size="small"
              disabled={disabled}
              onClick={() => setRows(rows.filter((_, i) => i !== idx))}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Stack>
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={row.has_value}
                  disabled={disabled}
                  onChange={(e) => patchRow(idx, { has_value: e.target.checked })}
                />
              }
              label="Has value"
              sx={{ mr: 0 }}
            />
            {row.has_value && (
              <>
                <TextField
                  select
                  size="small"
                  label="Comparison"
                  sx={{ width: 210 }}
                  value={row.op}
                  disabled={disabled}
                  onChange={(e) => patchRow(idx, { op: e.target.value })}
                >
                  {FIELD_EXISTS_OPS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  size="small"
                  label="Value"
                  sx={{ flex: 1, minWidth: 180 }}
                  value={row.value}
                  disabled={disabled}
                  onChange={(e) => patchRow(idx, { value: e.target.value })}
                />
                <TextField
                  select
                  size="small"
                  label="Value mode"
                  sx={{ width: 190 }}
                  value={row.value_mode}
                  disabled={disabled}
                  onChange={(e) => patchRow(idx, { value_mode: e.target.value })}
                >
                  {VALUE_MODE_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </TextField>
              </>
            )}
          </Stack>
        </Box>
      ))}

      <Box>
        <Button
          size="small"
          startIcon={<AddIcon />}
          disabled={disabled}
          onClick={() => setRows([...rows, { ...EMPTY_ROW }])}
        >
          Add field check
        </Button>
      </Box>
    </Stack>
  );
}
